import { upperFirst } from "lodash";
import {
  AllergyRow,
  ChooseOneValue,
  GroupValue,
  HistoryRow,
  OptionGroupDef,
  RowTextValue,
  SummaryLine,
  VitalsValue,
} from "./readmission-f2f-types";
import { isGroupValue } from "./readmission-f2f-utils";
import {
  ALLERGY_FIELD_LABELS,
  ALLERGY_SEVERITY_OPTIONS,
  CURRENT_VISIT_BULLET,
  GROUP_NOTE_PREFIX,
  HISTORY_NOTES_LABEL,
  SENTENCE_END_PATTERN,
  SUMMARY_STATEMENT_MIN_WORDS,
} from "./readmission-f2f-constant";

const noteOf = (freeText: string): string[] => {
  const free = freeText.trim();
  return free ? [`${GROUP_NOTE_PREFIX}${free}`] : [];
};

export const formatSummaryLine = (line: SummaryLine): string =>
  typeof line === "string" ? line : `${line.title}: ${line.text}`;

// Typed text may already end its own sentence; only add the full stop when it does not.
export const endSentence = (text: string): string =>
  SENTENCE_END_PATTERN.test(text) ? text : `${text}.`;

const toTitledLines = (title: string, parts: string[]): SummaryLine[] =>
  parts.length ? [{ title, text: endSentence(parts.join(", ")) }] : [];

/** Narrative casing: first letter lowered unless the label starts with an acronym. */
export const toNarrativeCase = (label: string): string =>
  label && !/^[A-Z]{2}/.test(label)
    ? label[0].toLowerCase() + label.slice(1)
    : label;

const groupParts = (value: GroupValue): string[] => {
  const picks = value.selected.map((opt) => {
    const detail = value.details[opt]?.trim();
    const label = toNarrativeCase(opt);
    return detail ? `${label} (${detail})` : label;
  });
  return [...picks, ...noteOf(value.freeText)];
};

const withDetail = (label: string, detail?: string): string => {
  const text = detail?.trim();
  return text ? `${label} (${text})` : label;
};

const chooseOneParts = (value: ChooseOneValue): string[] =>
  [
    withDetail(toNarrativeCase(value.selected), value.detail),
    ...noteOf(value.freeText),
  ].filter(Boolean);

export const buildGroupEntries = (
  title: string,
  value: GroupValue | ChooseOneValue | undefined
): SummaryLine[] => {
  if (!value) return [];
  return toTitledLines(
    title,
    isGroupValue(value) ? groupParts(value) : chooseOneParts(value)
  );
};

/** The lead option reads into its ticked nested options: "Needs assistance with bathing, transferring". */
export const joinNestedPicks = (
  group: OptionGroupDef,
  value: GroupValue | ChooseOneValue | undefined
): GroupValue | ChooseOneValue | undefined => {
  const { nested } = group;
  if (!nested || !value || !isGroupValue(value)) return value;
  if (!value.selected.includes(nested.lead)) return value;
  const picks = value.selected.filter((it) => nested.options.includes(it));
  return {
    ...value,
    selected: [`${nested.lead} ${picks.map(toNarrativeCase).join(", ")}`],
  };
};

/** Group-aware variant: appends the selected option's helper text, as the reference note does. */
export const buildOptionGroupEntries = (
  group: OptionGroupDef,
  value: GroupValue | ChooseOneValue | undefined
): SummaryLine[] => {
  const entries = buildGroupEntries(group.title, joinNestedPicks(group, value));
  const helper =
    value && !isGroupValue(value)
      ? group.optionHelperText?.[value.selected]
      : undefined;
  if (!helper || !entries.length) return entries;
  return entries.map((line) =>
    typeof line === "string"
      ? line
      : { ...line, text: `${line.text} ${helper}` }
  );
};

const isStatement = (part: string) =>
  part.trim().split(/\s+/).length >= SUMMARY_STATEMENT_MIN_WORDS;

/**
 * Current Visit display only: the group title sits on its own line with each
 * statement-length selection as an indented bullet beneath it. Short
 * selections keep the comma-joined summary line. The saved note text is unchanged.
 */
export const buildOptionGroupDisplayEntries = (
  group: OptionGroupDef,
  value: GroupValue | ChooseOneValue | undefined
): SummaryLine[] => {
  if (!value || group.nested) return buildOptionGroupEntries(group, value);
  const parts = isGroupValue(value) ? groupParts(value) : chooseOneParts(value);
  if (parts.length < 2 || !parts.some(isStatement))
    return buildOptionGroupEntries(group, value);
  return [
    { title: group.title, text: "" },
    ...parts.map((part) => ({
      title: "",
      text: `${CURRENT_VISIT_BULLET}${upperFirst(part)}`,
      isNested: true,
    })),
  ];
};

export const buildGroupLine = (
  title: string,
  value: GroupValue | ChooseOneValue | undefined
): string[] => buildGroupEntries(title, value).map(formatSummaryLine);

export const buildRowTextEntries = (value: RowTextValue): SummaryLine[] =>
  Object.entries(value ?? {})
    .filter(([, entry]) => entry.checked)
    .map(([key, entry]) =>
      entry.text.trim()
        ? { title: key, text: endSentence(entry.text.trim()) }
        : `${key}.`
    );

export const splitFreeTextLines = (text = ""): string[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

export const buildRowTextLines = (value: RowTextValue): string[] =>
  buildRowTextEntries(value).map(formatSummaryLine);

/** The section's "Additional note" box as its own untitled lines, one per typed line; nothing when empty. */
export const buildFreeTextEntry = (freeText = ""): SummaryLine[] =>
  splitFreeTextLines(freeText);

const nestedEntry = (title: string, text: string): SummaryLine[] =>
  text.trim() ? [{ title, text: text.trim(), isNested: true }] : [];

export const buildAllergyEntries = (
  rows: AllergyRow[],
  freeText = ""
): SummaryLine[] => [
  ...rows
    .filter((row) => row.allergy.trim())
    .flatMap((row, index): SummaryLine[] => [
      {
        title: `${index + 1}. ${ALLERGY_FIELD_LABELS.allergy}`,
        text: row.allergy.trim(),
      },
      ...nestedEntry(
        ALLERGY_FIELD_LABELS.severity,
        ALLERGY_SEVERITY_OPTIONS.find((it) => it.value === row.severity)
          ?.label ?? ""
      ),
      ...nestedEntry(ALLERGY_FIELD_LABELS.notes, row.notes),
    ]),
  ...buildFreeTextEntry(freeText),
];

export const buildHistoryEntries = (
  rows: HistoryRow[],
  nameLabel: string,
  freeText = ""
): SummaryLine[] => [
  ...rows
    .filter((row) => row.name.trim())
    .flatMap((row, index): SummaryLine[] => [
      { title: `${index + 1}. ${nameLabel}`, text: row.name.trim() },
      ...nestedEntry(HISTORY_NOTES_LABEL, row.notes),
    ]),
  ...buildFreeTextEntry(freeText),
];

export const buildVitalsLines = (value: VitalsValue): string[] =>
  Object.entries(value ?? {})
    .map(([, row]) => row)
    .filter((row) => row.value.trim() || row.unable)
    .map((row) => row.value.trim());
